import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { Card } from '../../components/ui/Card';
import { useTemplateMigration, templateMigrationData } from '../../core/utils/migrateTemplates';
import { Badge } from '../../components/ui/Badge';
import { Database, CheckCircle, XCircle } from 'lucide-react';

/**
 * One-time migration page to move templates from file-based storage to Firebase database
 * This page should be accessed once to perform the migration, then can be removed or hidden
 */
export const TemplateMigrationPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    migrateAll,
    migrateNda,
    migrateProfessionalServices,
    migrateSaasProposal,
    checkExisting,
  } = useTemplateMigration();
  const [isMigrating, setIsMigrating] = useState(false);
  const [result, setResult] = useState<{
    ndaId?: string;
    professionalServicesId?: string;
    saasProposalId?: string;
    errors: string[];
  } | null>(null);

  const existing = checkExisting();

  const handleMigrateAll = async () => {
    if (
      !confirm(
        'This will create NDA, Professional Services, and SaaS Proposal templates in the database. Continue?'
      )
    ) {
      return;
    }

    setIsMigrating(true);
    setResult(null);

    try {
      const migrationResult = await migrateAll();
      setResult(migrationResult);
    } catch (error: any) {
      setResult({
        errors: [error.message || 'Migration failed'],
      });
    } finally {
      setIsMigrating(false);
    }
  };

  const success = result && result.errors.length === 0;
  const hasErrors = result && result.errors.length > 0;

  const templates = useMemo(
    () => [
      {
        key: 'nda' as const,
        title: templateMigrationData.nda.name,
        description: 'Standard NDA template for protecting confidential information',
        fieldsCount: templateMigrationData.nda.dynamicFields.length,
        exists: existing.ndaExists,
        migrate: migrateNda,
      },
      {
        key: 'professionalServices' as const,
        title: templateMigrationData.professionalServices.name,
        description:
          'Standard Professional Services Agreement template for client engagements',
        fieldsCount: templateMigrationData.professionalServices.dynamicFields.length,
        exists: existing.professionalServicesExists,
        migrate: migrateProfessionalServices,
      },
      {
        key: 'saasProposal' as const,
        title: templateMigrationData.saasProposal.name,
        description:
          'Standard SaaS proposal template with editable scope, pricing, and signature blocks.',
        fieldsCount: templateMigrationData.saasProposal.dynamicFields.length,
        exists: existing.saasProposalExists,
        migrate: migrateSaasProposal,
      },
    ],
    [
      existing.ndaExists,
      existing.professionalServicesExists,
      existing.saasProposalExists,
      migrateNda,
      migrateProfessionalServices,
      migrateSaasProposal,
    ]
  );

  const handleMigrateSingle = async (key: 'nda' | 'professionalServices' | 'saasProposal') => {
    const template = templates.find((t) => t.key === key);
    if (!template) return;

    if (template.exists) {
      const proceed = confirm(
        `${template.title} already exists for this client. Create another copy anyway?`
      );
      if (!proceed) return;
    }

    setIsMigrating(true);
    try {
      const id = await template.migrate();
      setResult((prev) => ({
        errors: prev?.errors ?? [],
        ...(prev || {}),
        [`${key}Id`]: id,
      }) as any);
    } catch (error: any) {
      setResult((prev) => ({
        ...(prev || { errors: [] }),
        errors: [...(prev?.errors ?? []), error.message || `Migration failed for ${template.title}`],
      }));
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Migrate Templates to Database"
        description="Move NDA, Professional Services, and SaaS Proposal templates from file-based storage to Firebase"
        icon={Database}
        actions={
          <Button variant="outline" onClick={() => navigate('/documents/templates')}>
            Back to Templates
          </Button>
        }
      />

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Migration Overview</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will create three templates in your Firebase database:
            </p>
            <div className="grid gap-4 md:grid-cols-3 mb-4">
              {templates.map((t) => (
                <div key={t.key} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-sm">{t.title}</h4>
                    <Badge variant={t.exists ? 'success' : 'outline'}>
                      {t.exists ? 'Already exists' : 'Not migrated'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600">{t.description}</p>
                  <p className="text-xs text-gray-500">{t.fieldsCount} dynamic fields</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isMigrating}
                    onClick={() => handleMigrateSingle(t.key)}
                    className="mt-2 w-full"
                  >
                    {t.exists ? 'Migrate another copy' : 'Migrate this template'}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              Templates will be associated with your current client. The original template files will remain as backups.
            </p>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleMigrateAll}
              loading={isMigrating}
              disabled={isMigrating}
              className="w-full sm:w-auto"
            >
              {isMigrating ? 'Migrating...' : 'Migrate All Templates'}
            </Button>
          </div>

          {result && (
            <div className="pt-4 space-y-4">
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-2">Migration Successful!</h4>
                      <div className="text-sm text-green-800 space-y-1">
                        {result.ndaId && (
                          <p>✓ NDA template created (ID: {result.ndaId})</p>
                        )}
                        {result.professionalServicesId && (
                          <p>✓ Professional Services template created (ID: {result.professionalServicesId})</p>
                        )}
                        {result.saasProposalId && (
                          <p>✓ SaaS Proposal template created (ID: {result.saasProposalId})</p>
                        )}
                        <p className="mt-3">
                          You can now find these templates in the Templates list. The system will use the database versions instead of file-based templates.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {hasErrors && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900 mb-2">Migration Errors</h4>
                      <ul className="text-sm text-red-800 space-y-1">
                        {result.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {result.ndaId && result.professionalServicesId && result.saasProposalId && !hasErrors && (
                <div className="pt-4">
                  <Button onClick={() => navigate('/documents/templates')}>
                    View Templates
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
